import React, { useState, useEffect } from 'react';

const SkinTips = ({ className = '' }) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const skinCareTips = [
    "Always wear sunscreen with at least SPF 30, even on cloudy days.",
    "Drink 8 glasses of water daily to keep your skin hydrated from within.",
    "Never sleep with makeup on - it clogs pores and accelerates aging.",
    "Exfoliate 2-3 times a week to remove dead skin cells.",
    "Apply moisturizer while your skin is still damp to lock in hydration.",
    "Get 7-9 hours of sleep for optimal skin cell regeneration.",
    "Use a silk pillowcase to reduce friction and prevent wrinkles.",
    "Avoid touching your face frequently to prevent bacteria transfer.",
    "Change your pillowcases weekly to avoid acne breakouts.",
    "Apply eye cream by gently patting, never rubbing.",
    "Use lukewarm water to wash your face - hot water strips natural oils.",
    "Apply products from thinnest to thickest consistency.",
    "Vitamin C serum in the morning brightens and protects skin.",
    "Retinol at night helps reduce fine lines and improve texture.",
    "Don't forget sunscreen on your neck, hands, and chest.",
    "Eat foods rich in omega-3 fatty acids for healthy skin.",
    "Exercise regularly to improve blood circulation to your skin.",
    "Use a humidifier in dry environments to prevent skin dehydration.",
    "Pat your skin dry instead of rubbing to avoid irritation.",
    "Apply sunscreen 15-30 minutes before sun exposure.",
    "Cleanse your face twice daily - morning and night.",
    "Use a separate face towel and wash it regularly.",
    "Antioxidants like vitamin E protect against environmental damage.",
    "Avoid smoking - it accelerates skin aging dramatically.",
    "Limit alcohol consumption as it dehydrates your skin.",
    "Stress management helps prevent acne and other skin issues.",
    "Use products suitable for your specific skin type.",
    "Test new products on a small area first to check for reactions.",
    "Green tea has anti-inflammatory properties great for skin.",
    "Protect your lips with SPF lip balm daily.",
    "Avoid over-exfoliating - it can damage your skin barrier.",
    "Use hyaluronic acid to boost skin moisture levels.",
    "Niacinamide helps reduce inflammation and minimize pores.",
    "Keep your phone screen clean to prevent transferring bacteria.",
    "Wear protective clothing in strong sun, not just sunscreen.",
    "Cold showers can help tighten pores and reduce inflammation.",
    "Facial massage improves circulation and lymphatic drainage.",
    "Don't pick at acne - it causes scarring and spreads bacteria.",
    "Use a clean makeup brush for application to avoid bacteria.",
    "Alpha hydroxy acids (AHAs) help with skin texture and tone.",
    "Beta hydroxy acids (BHAs) are excellent for oily, acne-prone skin.",
    "Apply neck cream upward to combat gravity's effects.",
    "Vitamin B3 helps strengthen your skin's protective barrier.",
    "Use sheet masks once a week for an intensive hydration boost.",
    "Avoid harsh scrubs - use gentle chemical exfoliants instead.",
    "Keep your skincare routine consistent for best results.",
    "Indoor plants can help improve air quality and skin health.",
    "Reduce sugar intake - it accelerates skin aging through glycation.",
    "Use ceramides to repair and maintain your skin barrier.",
    "Remember: healthy skin is a reflection of overall wellness."
  ];

  // Color palette for smooth transitions
  const colors = [
    { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900' },
    { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-900' },
    { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-900' },
    { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900' },
    { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900' },
    { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-900' },
  ];

  const currentColor = colors[currentTipIndex % colors.length];

  // Rotate tips every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentTipIndex((prevIndex) => (prevIndex + 1) % skinCareTips.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, [skinCareTips.length]);

  const handleNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentTipIndex((prevIndex) => (prevIndex + 1) % skinCareTips.length);
      setIsTransitioning(false);
    }, 300);
  };

  const handlePrevious = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentTipIndex((prevIndex) =>
        prevIndex === 0 ? skinCareTips.length - 1 : prevIndex - 1
      );
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <div className={`rounded-lg shadow-md p-6 transition-all duration-500 border-2 ${currentColor.bg} ${currentColor.border} ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-semibold ${currentColor.text} transition-colors duration-500`}>
          💡 Skin Care Tips
        </h3>
      </div>

      <div className="mb-4 min-h-[60px] flex items-center">
        <p className={`text-sm ${currentColor.text} leading-relaxed transition-all duration-300 ${isTransitioning ? 'opacity-0 transform translate-y-2' : 'opacity-100 transform translate-y-0'}`}>
          {skinCareTips[currentTipIndex]}
        </p>
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          className={`p-2 rounded-lg hover:bg-white/50 transition-colors ${currentColor.text}`}
          aria-label="Previous tip"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex space-x-1">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                i === (currentTipIndex % 3)
                  ? `${currentColor.border.replace('border-', 'bg-')} scale-125`
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        <button
          onClick={handleNext}
          className={`p-2 rounded-lg hover:bg-white/50 transition-colors ${currentColor.text}`}
          aria-label="Next tip"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SkinTips;